import {
  Box,
  Button,
  IconButton,
  ListSubheader,
  MenuItem,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutline';

import {
  MAX_ROUND,
  SCHEDULE_STAGES,
  createScheduleRow,
  createScheduleStageRow,
  nextScheduleSelection,
  normalizeStage,
  scheduleEntryLabel
} from '../../helpers/roundSchedule';

const selectionValue = (row) => {
  const round = String(row?.round ?? '').trim();
  if (round) {
    return `round:${round}`;
  }

  const stage = String(row?.stage ?? '').trim();
  return stage ? `stage:${stage}` : '';
};

// Controlled editor for event.roundSchedule. The wizard owns the rows; this
// component only reports whole-row changes so the normaliser stays the single
// source of validation.
const RoundScheduleEditor = ({
  rows = [],
  onChange,
  disabled = false,
  gameFormatRounds = 0,
  error = ''
}) => {
  const formatRounds = Math.floor(Number(gameFormatRounds) || 0);
  const nextSelection = nextScheduleSelection(rows, formatRounds);
  const canAdd = nextSelection !== null && rows.length < MAX_ROUND;

  const usedRounds = new Set();
  const usedStages = new Set();
  const roundNumbers = new Set();

  for (let round = 1; round <= Math.min(MAX_ROUND, formatRounds); round += 1) {
    roundNumbers.add(round);
  }

  rows.forEach((row) => {
    const round = Number(row?.round);
    if (String(row?.round ?? '').trim() !== '' && Number.isInteger(round)) {
      usedRounds.add(round);
      roundNumbers.add(round);
    }

    const stage = normalizeStage(row?.stage);
    if (stage) {
      usedStages.add(stage);
    }
  });

  const roundOptions = Array.from(roundNumbers).sort((a, b) => a - b);

  const updateRow = (id, patch) => {
    onChange(rows.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  const removeRow = (id) => {
    onChange(rows.filter((row) => row.id !== id));
  };

  const changeSelection = (row, selection) => {
    if (selection.startsWith('stage:')) {
      updateRow(row.id, { stage: selection.slice('stage:'.length), round: '' });
      return;
    }

    if (selection.startsWith('round:')) {
      updateRow(row.id, { round: selection.slice('round:'.length), stage: '' });
    }
  };

  const addRow = () => {
    if (!canAdd) {
      return;
    }

    const separator = nextSelection.indexOf(':');
    const kind = nextSelection.slice(0, separator);
    const value = nextSelection.slice(separator + 1);

    onChange([
      ...rows,
      kind === 'stage' ? createScheduleStageRow(value) : createScheduleRow(value)
    ]);
  };

  return (
    <Stack gap={1.5}>
      <Typography variant="caption" color="text.secondary" display="block">
        {formatRounds > 0
          ? `The game format has ${formatRounds} round${formatRounds === 1 ? '' : 's'}. `
          : ''}
        Choose a group round or a knockout stage and add its date and start
        time; rows without either are ignored.
      </Typography>

      {rows.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No round times added yet.
        </Typography>
      )}

      {rows.map((row, index) => {
        const label = scheduleEntryLabel(row) || `Row ${index + 1}`;
        const current = selectionValue(row);
        const currentStage = String(row.stage ?? '').trim();
        const unknownStage =
          currentStage && !normalizeStage(currentStage) ? currentStage : '';

        return (
          <Box
            key={row.id}
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'center' },
              gap: 1.5
            }}>
            <TextField
              select
              label="Round or stage"
              size="small"
              value={current}
              onChange={(event) => changeSelection(row, event.target.value)}
              disabled={disabled}
              sx={{ width: { xs: '100%', sm: 160 }, flexShrink: 0 }}>
              {!current && (
                <MenuItem value="" disabled>
                  Choose…
                </MenuItem>
              )}
              {unknownStage && (
                <MenuItem value={`stage:${unknownStage}`}>
                  {unknownStage} (not recognised)
                </MenuItem>
              )}
              {roundOptions.length > 0 && <ListSubheader>Group rounds</ListSubheader>}
              {roundOptions.map((round) => (
                <MenuItem
                  key={`round:${round}`}
                  value={`round:${round}`}
                  disabled={current !== `round:${round}` && usedRounds.has(round)}>
                  Round {round}
                </MenuItem>
              ))}
              <ListSubheader>Knockout stages</ListSubheader>
              {SCHEDULE_STAGES.map((stage) => (
                <MenuItem
                  key={`stage:${stage}`}
                  value={`stage:${stage}`}
                  disabled={current !== `stage:${stage}` && usedStages.has(stage)}>
                  {stage}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Date"
              size="small"
              type="date"
              value={row.date}
              onChange={(event) => updateRow(row.id, { date: event.target.value })}
              disabled={disabled}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: 1, minWidth: 0 }}
            />
            <TextField
              label="Start time"
              size="small"
              type="time"
              value={row.startTime}
              onChange={(event) =>
                updateRow(row.id, { startTime: event.target.value })
              }
              disabled={disabled}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: 1, minWidth: 0 }}
            />
            <IconButton
              aria-label={`Remove ${label}`}
              onClick={() => removeRow(row.id)}
              disabled={disabled}
              sx={{ alignSelf: { xs: 'flex-end', sm: 'center' } }}>
              <DeleteIcon />
            </IconButton>
          </Box>
        );
      })}

      <Box>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={addRow}
          disabled={disabled || !canAdd}>
          Add round or stage
        </Button>
      </Box>

      {error && (
        <Typography variant="caption" color="error" display="block">
          {error}
        </Typography>
      )}
    </Stack>
  );
};

export default RoundScheduleEditor;
